import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils.js";

/**
 * Liquid-glass pill button for the sign-in card. The surface, rim, hover sheen and
 * press motion live in tokens.css under `.glass-button`, beside the glass tokens they
 * read; this file only picks the variant and the layout. Use it on a
 * `.login-glass-card`, not for app-wide actions.
 *
 * Submit buttons keep their native type, so Enter-to-submit and password-manager
 * behaviour are unchanged; this is not an AsyncButton.
 */
const glassButtonVariants = cva(
  [
    "glass-button inline-flex h-11 w-full cursor-pointer select-none items-center justify-center gap-2.5 rounded-full text-[14.5px] font-semibold",
    "focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-(--focus-ring)",
    "disabled:cursor-not-allowed disabled:saturate-[0.7]",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        pill: "glass-button--pill text-[color:var(--ink)]",
        cta: "glass-button--cta text-white",
      },
    },
    defaultVariants: { variant: "pill" },
  }
);

export interface GlassButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof glassButtonVariants> {}

const GlassButton = React.forwardRef<HTMLButtonElement, GlassButtonProps>(
  ({ className, variant, type, ...props }, ref) => (
    <button
      ref={ref}
      // A glass button with no type would submit the sign-in form when it means
      // to act on its own, so it defaults to "button" like the base Button.
      type={type ?? "button"}
      className={cn(glassButtonVariants({ variant }), className)}
      {...props}
    />
  )
);
GlassButton.displayName = "GlassButton";

export { GlassButton, glassButtonVariants };
